<script lang="ts" setup>
import {
  TechDataVehicle,
  Groups,
  DataPoint,
  Weights,
  Body,
  LoadingSpaceDimensions,
  VehicleDimensions,
} from '@/types';
import {
  accordionNames,
  weightsValues,
  bodyValues,
  loadingSpaceDimensionsValues,
  vehicleDimensionsValues,
} from '../utils/accordionLabels';
import TdValuesItem from '@/components/td-values-item.vue';
import { getDefaultLocaleValue } from '../locales/default';
import { pushDataLayerEvent } from '@/utils/tracking';

import { computed, onMounted, ref } from 'vue';
import { WbAccordion, WbAccordionItem } from '../../ssr/wb';

const props = defineProps<{
  vehicle: TechDataVehicle;
  translations: Record<string, string>;
  locale: string;
  modelSeries: string;
}>();
const isSSR = ref(true);

onMounted(() => {
  setTimeout(() => {
    isSSR.value = false;
  }, 600);
});

const computed_accordions = computed(() => {
  return accordionNames
    .map((accordion) => props.vehicle[accordion as keyof Groups])
    .filter(
      (accordion) => accordion != undefined && !areAllKeysNull(accordion),
    );
});

const accordionLabels = computed(() => {
  return Object.keys(props.vehicle)
    .filter((key) => !areAllKeysNull(props.vehicle[key as keyof Groups]))
    .filter((key) => accordionNames.includes(key));
});

const statsLabels = (accordion: any) => {
  return Object.keys(accordion as Object);
};

function getDataPoints(
  accordion:
    | VehicleDimensions
    | LoadingSpaceDimensions
    | Weights
    | Body
    | undefined,
) {
  const dataPoints: DataPoint[] = [];
  const weights = accordion as Weights;
  if (Object.keys(weights).some((value) => weightsValues.includes(value))) {
    Object.values(weights).forEach((dataPoint) => {
      if (dataPoint) {
        dataPoints.push(dataPoint);
      }
    });
  }

  const body = accordion as Body;
  if (Object.keys(body).some((value) => bodyValues.includes(value))) {
    Object.values(body).forEach((dataPoint) => {
      if (dataPoint) {
        dataPoints.push(dataPoint);
      }
    });
  }
  const loadingSpace = accordion as LoadingSpaceDimensions;

  if (
    Object.keys(loadingSpace).some((value) =>
      loadingSpaceDimensionsValues.includes(value),
    )
  ) {
    Object.values(loadingSpace).forEach((dataPoint) => {
      if (dataPoint) {
        dataPoints.push(dataPoint);
      }
    });
  }

  const vehicleDimensions = accordion as VehicleDimensions;
  if (
    Object.keys(vehicleDimensions).some((value) =>
      vehicleDimensionsValues.includes(value),
    )
  ) {
    Object.values(vehicleDimensions).forEach((dataPoint) => {
      if (dataPoint) {
        dataPoints.push(dataPoint);
      }
    });
  }
  return dataPoints;
}
function areAllKeysNull(obj: any) {
  for (let key in obj) {
    if (key !== 'label' && obj.hasOwnProperty(key) && obj[key] !== null) {
      return false;
    }
  }
  return true;
}

const trackTechDataStats = (accordionLabel: any, index: any) => {
  const label =
    'expand|' +
    accordionLabel.replace(/([A-Z])/g, '_$1').toLowerCase() +
    '|' +
    (index + 1);

  pushDataLayerEvent({
    name: 'show_more_details',
    category: 'feature',
    action: 'cvans_techdata_component',
    label: label,
    vehicle: {
      modelCode: props.modelSeries,
      class: props.vehicle.vehicleClass,
      bodyType: props.vehicle.bodyType,
      baumusterId: props.vehicle.baumuster,
      type: 'van',
      category: 'commercial',
    },
  });
};
</script>

<template>
  <div class="owvans-technical-data">
    <WbAccordion
      :ssrAttrs="{
        class: 'wb-ssr__accordion',
      }"
      prevent-mobile-flyout
    >
      <WbAccordionItem
        v-for="(accordion, accordionIndex) in computed_accordions"
        :ssrAttrs="{
          class: 'wb-ssr__accordion-item',
        }"
        :key="accordion + '-accordion'"
        @wbopen="
          trackTechDataStats(accordionLabels[accordionIndex], accordionIndex)
        "
      >
        <h3
          slot="header"
          class="wb-accordion-toggle__caption"
          :data-test-id="`owvans-tech-data-accordion-header-${accordionIndex}`"
        >
          {{
            props.translations[
              `owvc_tech_data_${accordionLabels[accordionIndex]}`
            ] || getDefaultLocaleValue(accordionLabels[accordionIndex])
          }}
        </h3>

        <span v-show="!isSSR" slot="content">
          <div class="wb-grid-row">
            <TdValuesItem
              v-for="(dataPoint, key) in getDataPoints(accordion)"
              :key="key"
              :data-point="dataPoint"
              :locale="locale"
              :parentLabel="accordionLabels[accordionIndex]"
              :label="statsLabels(accordion)[key]"
              :translations="translations"
              mq1="12"
              mq3="6"
              mq5="3"
              :data-test-id="`owvans-tech-data-accordion-${accordionIndex}-datapoint-${key}`"
            />
          </div>
        </span>
      </WbAccordionItem>
    </WbAccordion>
  </div>
</template>

<style lang="scss" scoped>
@import '@workbench/core/dist/scss/utility.scss';
.wb-ssr__accordion {
  --focus-color: var(--wb-transparent);
  border-top: none;
  &:first-child {
    border-top: 1px solid var(--wb-grey-85);
  }
}

.wb-accordion-toggle__caption {
  font-size: 18px;
  margin: 0;
}
.wb-grid-row {
  @include wb-spacing('padding-left', 's');
}

.wb-ssr__accordion-item {
  height: 90px;
  background-color: rgb(248, 248, 248);
  padding: 20px;
  border-bottom: 1px solid #dbdbdb;
  display: flex;
  align-items: center;
}
</style>
